/**
 * Shared helpers for the Components-form row UI.
 *
 * Iter220 — Extracted verbatim from `AppraisalComponentsTab.js` so the
 * Repairs builders (Iron / Concrete / Tin-Tile) can adopt the same
 * row UX in Iter221+. Zero behaviour change vs. the inlined originals.
 *
 * Exports:
 *   • WORK_TYPES, PREFIX_BY_TYPE, RATE_MODAL_PRESET_BY_SECTION
 *   • MATERIAL_SECTIONS, MATERIAL_TOKENS_BY_SECTION, MATERIAL_DISPLAY_LABEL,
 *     PRIMARY_TO_SECTION_OVERRIDE, PRIMARY_TO_GUTTER_MATERIAL
 *   • UNIT_BASED_TYPES, QUOTE_TYPE_TO_MATERIAL
 *   • detectMaterialFor, buildMaterialBuckets
 *   • inferMaterialFromContext, inferTypeName
 *   • fmt, buildSheetProfileMap, lineTotals
 *   • MITRE_TAG, AUTO_BRACKET_TAG, isMitreLine, isAutoBracketLine,
 *     stripTag, withMitreTag
 */

// Map AppraisalComponentsTab section keys to the IronRateDetailModal preset
import { canonicalMaterial } from '@/lib/materials';

// key. The modal supports 7 presets (sheet/ridge/barge/valley/flashing/
// fastening/tile); every other section falls back to `sheet`.
export const RATE_MODAL_PRESET_BY_SECTION = {
  Sheet: 'sheet',
  Ridge: 'ridge',
  Barge: 'barge',
  Valley: 'valley',
  Flashing: 'flashing',
  Tile: 'tile',
  Aquaseal: 'flashing',
};

export const WORK_TYPES = [
  { key: 'Cleaning',  colour: '#0EA5E9', prefix: 'Cl' },
  { key: 'Travel',    colour: '#6366F1', prefix: 'Tr' },
  { key: 'Urgent',    colour: '#DC2626', prefix: 'Ur' },
  { key: 'Safety',    colour: '#EAB308', prefix: 'Sa' },
  { key: 'Sheet',     colour: '#D35400', prefix: 'Sh' },
  { key: 'Tile',      colour: '#A16207', prefix: 'T'  },
  { key: 'Ridge',     colour: '#9333EA', prefix: 'R'  },
  { key: 'Valley',    colour: '#2563EB', prefix: 'V'  },
  { key: 'Barge',     colour: '#7C3AED', prefix: 'B'  },
  { key: 'Spouting',  colour: '#0891B2', prefix: 'Sp' },
  { key: 'Fastening', colour: '#DB2777', prefix: 'Fa' },
  { key: 'Flashing',  colour: '#059669', prefix: 'Fl' },
  { key: 'Underlay',  colour: '#475569', prefix: 'U'  },
  { key: 'Aquaseal',  colour: '#0D9488', prefix: 'AQ', label: 'Dektite' },
  { key: 'ReCoat',    colour: '#DC2626', prefix: 'Rc' },
  { key: 'Face',      colour: '#92400E', prefix: 'FA' },
  { key: 'Length',    colour: '#92400E', prefix: 'L'  },
  { key: 'Misc',      colour: '#64748B', prefix: 'M'  },
  { key: 'Other',     colour: '#475569', prefix: 'O'  },
];

export const PREFIX_BY_TYPE = WORK_TYPES.reduce((m, w) => { m[w.key] = w.prefix; return m; }, {});

export const MATERIAL_SECTIONS = ['Sheet', 'Tile', 'Ridge', 'Valley', 'Barge', 'Flashing', 'Fastening'];

// Token list per section. Order matters — LONGER tokens are checked first
// so `Aluguard` wins over `Alu`, `ZinaCore` over `Zinc`, `Concrete` over `Conc`.
export const MATERIAL_TOKENS_BY_SECTION = {
  Sheet:     ['Aluguard', 'ZinaCore', 'Zcore', 'Polycarbonate', 'Topglass', 'Eurolite', 'MagnaFlow', 'Maxam', 'Maxx', 'Alu',  'Galv', 'Zinc'],
  Ridge:     ['Aluguard', 'ZinaCore', 'ZCore', 'Zcore', 'Concrete', 'Conc', 'Clay', 'MagnaFlow', 'Maxam', 'Maxx', 'Alu',  'Galv', 'Zinc'],
  Valley:    ['Aluguard', 'ZinaCore', 'Zcore', 'MagnaFlow', 'Maxam', 'Maxx', 'Alu',  'Galv', 'Zinc'],
  Barge:     ['Aluguard', 'ZinaCore', 'Zcore', 'Concrete', 'Conc', 'Clay', 'MagnaFlow', 'Maxam', 'Maxx', 'Alu',  'Galv', 'Zinc'],
  Flashing:  ['Aluguard', 'ZinaCore', 'Zcore', 'MagnaFlow', 'Maxam', 'Maxx', 'Alu',  'Galv', 'Zinc', 'Copper'],
  Fastening: ['ColourTile', 'Colour', 'Galv', 'Aluminium', 'AR8'],
};

// Canonical display labels — collapse legacy spellings (rule book: src/lib/materials.js).
export const MATERIAL_DISPLAY_LABEL = {
  ZinaCore:   'Maxam',
  ZCore:      'Maxam',
  Zcore:      'Maxam',
  Maxx:       'Maxam',
  Aluguard:   'Alu',
  Topglass:   'Polycarbonate',
  Conc:       'Concrete',
  Colour:     'Coloured',
  ColourTile: 'Coloured',
  Aluminium:  'Coloured',
};

export const PRIMARY_TO_SECTION_OVERRIDE = {
  Fastening: {
    Maxam:    'Coloured',
    Maxx:     'Coloured',
    Zcore:    'Coloured',
    Aluguard: 'Coloured',
    MagnaFlow:'Coloured',
    Alu:      'Coloured',
    Concrete: 'Coloured',
    Galv:     'Galv',
    Zinc:     'Galv',
  },
};

export const PRIMARY_TO_GUTTER_MATERIAL = {
  Maxam:     'maxam',
  Maxx:      'maxam',
  Aluguard:  'maxam',
  Zcore:     'maxam',
  ZCore:     'maxam',
  MagnaFlow: 'magnaflow',
  Galv:      'galvanised',
  Zinc:      'zincalume',
  Alu:       'zincalume',
};

export function detectMaterialFor(section, rowName) {
  const tokens = MATERIAL_TOKENS_BY_SECTION[section] || [];
  const n = (rowName || '');
  for (const tok of tokens) {
    const re = new RegExp(`\\b${tok.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i');
    if (re.test(n)) return MATERIAL_DISPLAY_LABEL[tok] || tok;
  }
  return null;
}

// Sections whose master rates carry a curated `material` (Component Rate Tables cascade lists).
const MASTER_MATERIAL_SECTIONS = new Set(['Sheet', 'Ridge', 'Valley', 'Barge', 'Flashing']);
const MORTAR_RE = /\b(bed\s*(?:&|and)\s*point|point|cement)\b/i;

// Materials a rate row belongs to — master `material` first, name guess only as fallback.
// Mortar work (Bed & Point / Point / Cement End) serves both Concrete and Clay roofs.
export function materialsOfRate(section, rate) {
  const raw = MASTER_MATERIAL_SECTIONS.has(section) ? canonicalMaterial(rate?.material) : '';
  const m = raw || detectMaterialFor(section, rate?.type_name);
  if (!m) return [];
  if ((m === 'Concrete' || m === 'Clay') && MORTAR_RE.test(rate?.type_name || '')) return ['Concrete', 'Clay'];
  return [m];
}

export const rateHasMaterial = (section, rate, material) => materialsOfRate(section, rate).includes(material);

export function buildMaterialBuckets(section, rateRows) {
  const buckets = {};
  (rateRows || []).forEach(r => {
    materialsOfRate(section, r).forEach(m => { buckets[m] = (buckets[m] || 0) + 1; });
  });
  return Object.entries(buckets)
    .sort((a, b) => b[1] - a[1])
    .map(([k, n]) => ({ key: k, count: n }));
}

export const UNIT_BASED_TYPES = new Set([
  'Cleaning', 'Travel', 'Urgent', 'Safety', 'Tile', 'Fastening',
  'Underlay', 'Aquaseal', 'ReCoat', 'Misc', 'Other',
]);

export const QUOTE_TYPE_TO_MATERIAL = [
  { match: /bed\s*(and|&)?\s*point/i,        material: 'Bed & Point' },
  { match: /concrete/i,                       material: 'Concrete' },
  // Iter224 — REPAIRS on iron roofs default to Galv (existing roofs are
  // typically galvanised steel). Must come BEFORE the generic Iron rule
  // so Re-roof Iron still resolves to Maxam.
  { match: /(rescrew|repair|restoration).*(long\s*run|longrun|iron)/i, material: 'Galv' },
  { match: /(long\s*run|longrun|iron).*(rescrew|repair|restoration)/i, material: 'Galv' },
  { match: /(longrun|long\s*run|iron).*reroof|reroof.*(longrun|iron)/i, material: 'Maxam' },
  { match: /(longrun|long\s*run|iron)/i,      material: 'Maxam' },
  { match: /restoration/i,                    material: 'Concrete' },
  { match: /galv/i,                           material: 'Galv' },
  { match: /zinc/i,                           material: 'Zinc' },
  { match: /(maxam|maxx|maxispan|zcore|zinacore)/i, material: 'Maxam' },
  { match: /alu/i,                            material: 'Alu' },
];

export function inferMaterialFromContext({ quoteType, roofType, roofBrand }) {
  const q = String(quoteType || '');
  const rt = String(roofType || '').toLowerCase();
  const rb = String(roofBrand || '').toLowerCase();
  for (const rule of QUOTE_TYPE_TO_MATERIAL) {
    if (rule.match.test(q)) {
      if (/restoration/i.test(q) && rt && !/(concrete|tile|clay)/.test(rt)) continue;
      return rule.material;
    }
  }
  if (/concrete|tile|clay/.test(rt)) return 'Concrete';
  if (/long\s*run|iron|steel/.test(rt)) {
    if (/maxam|maxx|zcore|zinacore/.test(rb)) return 'Maxam';
    if (/aluguard/.test(rb)) return 'Alu';
    return 'Galv';
  }
  return '';
}

export function inferTypeName(rates, roofSubtype, material, roofBrand) {
  if (!Array.isArray(rates) || !rates.length || !material) return '';
  const subTokens = String(roofSubtype || '').toLowerCase().split(/\s+/).filter(Boolean);
  const brandTokens = String(roofBrand || '').toLowerCase().split(/\s+/).filter(Boolean);
  const matLc = material.toLowerCase();
  const matEsc = material.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const matRe = new RegExp(matLc === 'bed & point' ? 'bed\\s*(and|&)?\\s*point' : `\\b${matEsc}\\b`, 'i');
  let best = null;
  let bestScore = 0;
  for (const r of rates) {
    const nm = String(r.type_name || '');
    if (!matRe.test(nm)) continue;
    // Jun 2026 — never pick a rate linked to other roof types (e.g. Butynol Barging on an Iron roof).
    const rt = r.roof_type_links;
    if (Array.isArray(rt) && rt.length && !(roofSubtype && rt.includes(roofSubtype))) continue;
    const nmLc = nm.toLowerCase();
    let score = 1;
    for (const t of subTokens)  if (t && nmLc.includes(t)) score += 2;
    for (const t of brandTokens) if (t && t.length > 2 && nmLc.includes(t)) score += 1;
    if (score > bestScore) { best = r; bestScore = score; }
  }
  return best ? best.type_name : '';
}

export const fmt = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export function buildSheetProfileMap(labels) {
  const map = {};
  (labels || []).forEach(label => {
    const m = label.match(/^(.*?)\s*(\d+(?:\.\d+)?)\s*m?\s*$/);
    if (m) {
      const name = m[1].trim();
      const cov = parseFloat(m[2]);
      if (!map[name]) map[name] = [];
      if (!map[name].includes(cov)) map[name].push(cov);
    } else if (!map[label]) {
      map[label] = [];
    }
  });
  Object.values(map).forEach(a => a.sort((x, y) => x - y));
  return map;
}

// Iter149 — Spouting Bracket Summary helpers. Mitre rows and auto-
// generated Bracket rows are tagged via prefix markers in the line's
// existing `notes` field.
export const MITRE_TAG = '[MITRE]';
export const AUTO_BRACKET_TAG = '[AUTO:bracket]';
export const isMitreLine = (l) => /^\[MITRE\]/i.test((l && l.notes) || '');
export const isAutoBracketLine = (l) => /^\[AUTO:(bracket|dropper|stop_end|mitre|rivet|silicone|silicone_modified|silicone_clear)\]/i.test((l && l.notes) || '');
// Spouting: gutter-length rows vs accessory rows (brackets, stop ends, outlets, downpipes …).
export const SPOUT_ACCESSORY_RE = /bracket|stop\s*end|mitre|dropper|outlet|downpipe|clip|screw|bend|joiner|rivet|silicone|saddle|offset|elbow|corner/i;
export const isGutterLengthLine = (l) => !isAutoBracketLine(l) && !SPOUT_ACCESSORY_RE.test((l && l.type_name) || '');
export const spoutRatePatch = (r) => ({
  type_name: r.type_name, buy_cost: Number(r.buy_cost) || 0, materials_rate: Number(r.materials_rate) || 0,
  labour_rate: Number(r.labour_rate) || 0, contractor_rate: Number(r.contractor_rate) || 0,
  supplier: r.supplier || '', supplier_code: r.supplier_code || '',
});
// Spouting rate rows for a Gutter Details context (same words the catalogue uses).
export function spoutingRowsFor(rates, { gutterType, profileLabel, materialLabel }, gutterOnly = false) {
  const prof = String(profileLabel || '').toLowerCase().trim();
  let mat = String(materialLabel || '').toLowerCase().trim();
  if (mat.startsWith('galv')) mat = 'galv';
  const marley = gutterType === 'marley';
  return (rates || []).filter(r => {
    const tn = String(r.type_name || '').toLowerCase();
    if (marley !== tn.includes('marley')) return false;
    if (gutterOnly && SPOUT_ACCESSORY_RE.test(tn)) return false;
    if (gutterOnly && !(marley ? tn.includes('spouting') : /^gutter\s/.test(tn))) return false;
    if (prof && !tn.includes(prof)) return false;
    if (!marley && mat && !tn.includes(mat)) return false;
    return true;
  });
}
export const stripTag = (notes, tag) => (notes || '').replace(new RegExp(`^\\s*${tag.replace(/[[\]]/g, '\\$&')}\\s*`, 'i'), '');
export const withMitreTag = (notes, on) => on
  ? `${MITRE_TAG} ${stripTag(notes, MITRE_TAG)}`.trim()
  : stripTag(notes, MITRE_TAG).trim();

export function lineTotals(line, mitreExtraM = 0) {
  const qty = Number(line.quantity) || 0;
  const length = Number(line.length) || 0;
  // iter313f — Face-pin area-only override. When set, the operator
  // entered a raw m² on the pin instead of L+W; [Total] shows that
  // area directly instead of qty×length. Cleared by setting to 0.
  const override = Number(line.total_m2_override) || 0;
  const base = override > 0
    ? override
    : (length > 0 ? qty * length : qty);
  const mitreCount = Math.max(0, Math.min(2, Number(line.mitre_count) || 0));
  const extra = mitreCount * (Number(mitreExtraM) || 0);
  const total = base + extra;
  const buy = Number(line.buy_cost) || 0;
  const mat = Number(line.materials_rate) || 0;
  const lab = Number(line.labour_rate) || 0;
  const con = Number(line.contractor_rate) || 0;
  return {
    line_total: total,
    base_length: base,
    mitre_extra_length: extra,
    customer_charge: total * (mat + lab),
    cost_to_us: total * (buy + con),
    materials_profit: total * (mat - buy),
    labour_profit: total * (lab - con),
  };
}


// ────────────────────────────────────────────────────────────────────────
// Iter286 — Polycarbonate stock-length snap (pure-JS mirror of
// `backend/services/stock_lengths.py::snap_run`).
//
// Given a measured run and a ladder of supplier stock lengths, return
//   { measured, billed, sticksUsed }
// so the Components row can show a live "3.0m stick / 0.10m waste" pill
// without hitting the API on every keystroke.
//
// Rules (must match backend):
//   • measured ≤ 0 or empty ladder → billed 0, no sticks.
//   • measured ≤ min stock → billed = smallest stock.
//   • measured ≤ some stock → billed = smallest stock ≥ measured.
//   • measured > max stock → repeat the longest stick until covered.
// ────────────────────────────────────────────────────────────────────────
export function snapRun(measured, stockLengths) {
  const m = Math.max(Number(measured) || 0, 0);
  const ladder = Array.from(new Set(
    (stockLengths || [])
      .map(x => Math.round((Number(x) || 0) * 1000) / 1000)
      .filter(x => x > 0),
  )).sort((a, b) => a - b);
  if (m <= 0 || ladder.length === 0) {
    return { measured: Math.round(m * 1000) / 1000, billed: 0, sticksUsed: [] };
  }
  for (const L of ladder) {
    if (L + 1e-6 >= m) {
      return { measured: Math.round(m * 1000) / 1000, billed: L, sticksUsed: [L] };
    }
  }
  const Lmax = ladder[ladder.length - 1];
  const nFull = Math.ceil(m / Lmax);
  const sticks = Array(nFull).fill(Lmax);
  const billed = sticks.reduce((s, x) => s + x, 0);
  return {
    measured: Math.round(m * 1000) / 1000,
    billed:   Math.round(billed * 1000) / 1000,
    sticksUsed: sticks,
  };
}
